import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SlideOver from "../../../common/components/SlideOver/SlideOver";
import type { RootState } from "../../../common/state/store";
import type { IMedia } from "src/model/unityObject";
import { Tabs, Tab, Box } from "@mui/material";
import { getSubscriptionFeature } from "src/features/subscription/state/subscriptionSlice";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getCurrentUser } from "../../profile/state/profileSlice";
import { fetchBlockadeLabsImageModels, getBlockadeLabsModels } from "../state/gallerySlice";
import { GenerateBlockadeLabsImage } from "./GenerateBlockadeLabsImage";
import { CreateAvatar } from "./CreateAvatar";

import GenerateTtsPanel from "./GenerateTtsPanel";

export const GENERATE_MEDIA_PANEL_COMPONENT = "generateMediaPanel";
export const FETCH_AI_MODELS_FROM_BLOCKADE_LABS = "fetchAiModelsFromBlockadeLabs";

export const GenerateMediaPanel = ({
  open,
  onClose,
  onMediaCreated,
}: {
  open: boolean;
  onClose: () => void;
  onMediaCreated: (media: IMedia) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedTab, setSelectedTab] = useState(0);

  const currentUser = useAppSelector(getCurrentUser);

  const blockadeLabsFeature = useAppSelector((state: RootState) =>
    getSubscriptionFeature(state, "blockade-labs-integration"),
  );

  const isAllowedToUseBlockadeLabsIntegration =
    blockadeLabsFeature?.IsEnabled && currentUser?.role?.name === "Creator";

  const blockadeLabsModels = useAppSelector(getBlockadeLabsModels);

  const isGeneratePanelOpen = open;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  useEffect(() => {
    if (
      isGeneratePanelOpen &&
      isAllowedToUseBlockadeLabsIntegration &&
      blockadeLabsModels.length === 0
    ) {
      void dispatch(
        fetchBlockadeLabsImageModels({ componentId: FETCH_AI_MODELS_FROM_BLOCKADE_LABS }),
      );
    }
  }, [isGeneratePanelOpen, isAllowedToUseBlockadeLabsIntegration]);

  return (
    <SlideOver large open={open} onClose={onClose}>
      <div className="flex flex-col h-full px-6 pt-8 pb-3 relative">
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label={t("pages.gallery.generateMediaPanel.voiceOver")} />
          {isAllowedToUseBlockadeLabsIntegration && (
            <Tab label={t("pages.gallery.generateMediaPanel.360Image")} />
          )}
          <Tab label={t("general.avatar")} />
        </Tabs>
        <Box className="flex-1 min-h-0 flex flex-col">
          {selectedTab === 0 && (
            <GenerateTtsPanel isOpen={open} onClose={onClose} onMediaCreated={onMediaCreated} />
          )}

          {selectedTab === 1 && isAllowedToUseBlockadeLabsIntegration && (
            <GenerateBlockadeLabsImage
              models={blockadeLabsModels}
              onClose={onClose}
              onMediaCreated={onMediaCreated}
            />
          )}

          {selectedTab === (isAllowedToUseBlockadeLabsIntegration ? 2 : 1) && (
            <CreateAvatar isOpen={open} onClose={onClose} onMediaCreated={onMediaCreated} />
          )}
        </Box>
      </div>
    </SlideOver>
  );
};
